import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import NivaAvatar from '../NivaAvatar';
import featureFlagService from '../../services/featureFlagService';
import {
  LayoutDashboard,
  Settings,
  Users,
  HelpCircle,
  LogOut,
  Building,
  FileText,
  Wrench,
  CreditCard,
  UsersRound,
  BarChart2,
  HardHat,
  Menu,
  X,
  ShieldCheck,
  ClipboardList,
} from 'lucide-react';

// ─── Shared navigation content ────────────────────────────────────

const SidebarNav = ({ user, onLogout, onToggleAIAssistant, onNavigate }) => {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    featureFlagService.isFeatureEnabled('ADMIN_ENABLED').then(setIsAdmin).catch(() => {});
  }, []);

  const isActive = (path) => path === '/settings' || path === '/help'
    ? location.pathname === path
    : location.pathname.startsWith(path);
  const isLandlord = user?.userType === 'Landlord';
  const isTenant = user?.userType === 'Tenant';

  const handleNav = () => {
    if (onNavigate) onNavigate();
  };

  const SoonBadge = () => (
    <span className="ml-auto rounded-full bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700">
      Soon
    </span>
  );

  return (
    <div className="flex h-full max-h-screen flex-col gap-2">
      <div className="flex h-14 items-center border-b px-6">
        <Link className="flex items-center gap-2 font-semibold" to={isTenant ? '/niva' : '/dashboard'} onClick={handleNav}>
          <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
          <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Nivasapro.ai</span>
        </Link>
      </div>
      <ScrollArea className="flex-1 px-3">
        <div className="flex flex-col gap-2 py-2">
          <Button
            variant={isActive('/niva') ? "secondary" : "ghost"}
            className="justify-start gap-2"
            asChild
          >
            <Link to="/niva" onClick={handleNav}>
              <NivaAvatar size="xs" />
              Niva
            </Link>
          </Button>
          <Button
            variant={isActive('/dashboard') ? "secondary" : "ghost"}
            className="justify-start gap-2"
            asChild
          >
            <Link to="/dashboard" onClick={handleNav}>
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </Link>
          </Button>

          {/* Landlord-specific navigation */}
          {isLandlord && (
            <>
              <Button
                variant={isActive('/properties') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/properties" onClick={handleNav}>
                  <Building className="h-4 w-4" />
                  Properties
                </Link>
              </Button>
              <Button
                variant={isActive('/tenants') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/tenants" onClick={handleNav}>
                  <Users className="h-4 w-4" />
                  Tenants
                </Link>
              </Button>
              <Button
                variant={isActive('/maintenance') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/maintenance" onClick={handleNav}>
                  <Wrench className="h-4 w-4" />
                  Maintenance
                </Link>
              </Button>
              <Button
                variant={isActive('/providers') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/providers" onClick={handleNav}>
                  <HardHat className="h-4 w-4" />
                  Providers
                </Link>
              </Button>
              <Button
                variant={isActive('/applications') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/applications" onClick={handleNav}>
                  <ClipboardList className="h-4 w-4" />
                  Applications
                </Link>
              </Button>
              <Button
                variant={isActive('/niva-pay') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/niva-pay" onClick={handleNav}>
                  <CreditCard className="h-4 w-4" />
                  NivaPay
                </Link>
              </Button>
              <Button
                variant={isActive('/team') ? "secondary" : "ghost"}
                className="justify-start gap-2 w-full"
                asChild
              >
                <Link to="/team" onClick={handleNav}>
                  <UsersRound className="h-4 w-4" />
                  Team
                  <SoonBadge />
                </Link>
              </Button>
              <Button
                variant={isActive('/reports') ? "secondary" : "ghost"}
                className="justify-start gap-2 w-full"
                asChild
              >
                <Link to="/reports" onClick={handleNav}>
                  <BarChart2 className="h-4 w-4" />
                  Reports
                  <SoonBadge />
                </Link>
              </Button>
            </>
          )}

          {/* Tenant-specific navigation */}
          {isTenant && (
            <>
              <Button
                variant={isActive('/lease') ? "secondary" : "ghost"}
                className="justify-start gap-2 w-full"
                asChild
              >
                <Link to="/lease" onClick={handleNav}>
                  <FileText className="h-4 w-4" />
                  My Lease
                  <SoonBadge />
                </Link>
              </Button>
              <Button
                variant={isActive('/payments') ? "secondary" : "ghost"}
                className="justify-start gap-2 w-full"
                asChild
              >
                <Link to="/payments" onClick={handleNav}>
                  <CreditCard className="h-4 w-4" />
                  NivaPay
                </Link>
              </Button>
              <Button
                variant={isActive('/maintenance') ? "secondary" : "ghost"}
                className="justify-start gap-2"
                asChild
              >
                <Link to="/maintenance" onClick={handleNav}>
                  <Wrench className="h-4 w-4" />
                  Maintenance
                </Link>
              </Button>
            </>
          )}

          {isAdmin && isLandlord && (
            <>
              <Separator className="my-1" />
              <p className="px-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
              <Button
                variant={isActive('/admin') ? "secondary" : "ghost"}
                className="justify-start gap-2 w-full"
                asChild
              >
                <Link to="/admin/leads" onClick={handleNav}>
                  <ShieldCheck className="h-4 w-4 text-amber-600" />
                  Admin
                </Link>
              </Button>
            </>
          )}

          <Button
            variant={isActive('/settings') ? "secondary" : "ghost"}
            className="justify-start gap-2 w-full"
            asChild
          >
            <Link to="/settings" onClick={handleNav}>
              <Settings className="h-4 w-4" />
              Settings
            </Link>
          </Button>
        </div>
        <Separator className="my-2" />
        <div className="flex flex-col gap-2 py-2">
          <Button
            variant={isActive('/help') ? "secondary" : "ghost"}
            className="justify-start gap-2 w-full"
            asChild
          >
            <Link to="/help" onClick={handleNav}>
              <HelpCircle className="h-4 w-4" />
              Help Center
              <SoonBadge />
            </Link>
          </Button>
        </div>
      </ScrollArea>
      <div className="mt-auto p-4 border-t">
        <div className="mb-3 px-2">
          <p className="text-xs text-gray-500 mb-1">
            {user?.userType === 'Landlord' ? '🏢 Property Manager' : '🏠 Tenant'}
          </p>
          <p className="text-sm font-medium truncate">
            {user?.name?.trim() || user?.username}
          </p>
          {user?.companyName && (
            <p className="text-xs text-gray-500 truncate">{user.companyName}</p>
          )}
        </div>
        <Button variant="outline" className="w-full justify-start gap-2" onClick={() => { onLogout(); handleNav(); }}>
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );
};

// ─── Desktop Sidebar ──────────────────────────────────────────────

const Sidebar = ({ user, onLogout, onToggleAIAssistant }) => {
  return (
    <div className="hidden border-r bg-gray-100/40 lg:block dark:bg-gray-800/40 w-64 flex-shrink-0 h-screen sticky top-0">
      <SidebarNav user={user} onLogout={onLogout} onToggleAIAssistant={onToggleAIAssistant} />
    </div>
  );
};

// ─── Mobile Menu Button ───────────────────────────────────────────

const MobileMenuButton = ({ onClick }) => (
  <Button variant="ghost" size="icon" className="lg:hidden" onClick={onClick}>
    <Menu className="h-5 w-5" />
    <span className="sr-only">Open menu</span>
  </Button>
);

// ─── Mobile Sidebar Drawer ────────────────────────────────────────

const MobileSidebar = ({ open, onClose, user, onLogout, onToggleAIAssistant }) => {
  if (!open) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 w-64 bg-gray-100/95 dark:bg-gray-800/95 backdrop-blur-sm border-r shadow-xl animate-in slide-in-from-left duration-200">
        <div className="absolute right-2 top-3 z-10">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>
        <SidebarNav
          user={user}
          onLogout={onLogout}
          onToggleAIAssistant={onToggleAIAssistant}
          onNavigate={onClose}
        />
      </div>
    </div>
  );
};

export default Sidebar;
export { MobileMenuButton, MobileSidebar };
